import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { urlPath } from '../../_helpers/describe-error';
import { EVIDENCE_TOO_LARGE_MESSAGE } from '../../_constants/evidence-limits';
import { BOOKING_NOT_FOUND_MESSAGE } from '../../_constants/booking-not-found';
import { quietErrorsContext } from '../../_helpers/error-support.interceptor';

import * as CryptoJS from 'crypto-js';

@Injectable({
  providedIn: 'root'
})
export class BaseService {

  constructor(
    protected hashManagerService: SessionUserHashManagerService
  ) { }
  
  public prepareOptions(payload: any): any {

    //if (environment.envName != "dev") {
      this.hashManagerService.checkUser();
    //}

    let randomSalt = Math.floor(1000000000 + Math.random() * 9000000000).toString();
    let formattedPayload = payload == null ? "{}" : (typeof payload == "string") ? payload.toLowerCase() : JSON.stringify(payload).toLowerCase();
    let secret = environment.apiSecret + "-" + randomSalt.substr(-3) + "-" + formattedPayload;

    let httpHeaders = new HttpHeaders();
    let currentUser = JSON.parse(localStorage.getItem('currentUser'));
    if (currentUser && currentUser.token) {
      httpHeaders = httpHeaders
        .set('Content-Type', 'application/json')
        .set('Authorization', 'Bearer ' + currentUser.token)
        .set('RequestId', randomSalt)
        .set('RequestMatch', CryptoJS.SHA256(secret).toString(CryptoJS.enc.Hex));
    }

    return { headers: httpHeaders, observe: 'response' };
  }
  
  public prepareAnonymousOptions(payload: any): any {

    let randomSalt = Math.floor(1000000000 + Math.random() * 9000000000).toString();
    let formattedPayload = payload == null ? "{}" : (typeof payload == "string") ? payload.toLowerCase() : JSON.stringify(payload).toLowerCase();
    let secret = environment.apiSecret + "-" + randomSalt.substr(-3) + "-" + formattedPayload;

    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders
      .set('Content-Type', 'application/json')
      .set('RequestId', randomSalt)
      // .set('Authorization', 'Bearer ' + environment.apiKey)
      .set('RequestMatch', CryptoJS.SHA256(secret).toString(CryptoJS.enc.Hex));

    return { headers: httpHeaders, responseType: 'json', observe: 'response' };
  }

  public handleError(operation = 'operation') {
    return (error: HttpErrorResponse): Observable<any> => {
      if(environment.envName !== "prod") {
       console.error(`${operation} failed: ${error.message}`); // log to console
      }

      if (error.status === 422) {
        // Business-validation failure — the API's replacement for status 299
        // (TutorPortalAPI/Plans/retire-299-wire-swap.md A1). The body is RFC7807 ProblemDetails
        // with the user-facing message in `title`; the string fallback tolerates a bare-string
        // body during the transition. Checked FIRST so a string 422 body is not swallowed by
        // the generic string branch below without its isValidationError flag.
        throw {
          message: typeof error.error === 'string' ? error.error : error.error?.title,
          status: error.status,
          isValidationError: true
        };
      } else if (error.status === 413) {
        // A save too large to accept: evidence of work travels as base64 inside the save (40759). Shown as a
        // plain message the user can act on, not the generic one.
        throw {
          message: EVIDENCE_TOO_LARGE_MESSAGE,
          status: error.status,
          isValidationError: true
        };
      } else if (error.error && typeof error.error == "string") {
        throw {
          message: error.error,
          status: error.status
        };
      } else if (error.error && error.error.ClassName == "System.ComponentModel.DataAnnotations.ValidationException") {
        throw {
          message: error.error.Message,
          status: error.status
        };
      } else {
        // The call that failed travels with the error (path only, never the query), so an unhandled one is
        // reported with what it was rather than only the user-facing wording (bug 40429).
        throw  {
          message: "Oops, an error has occurred. If the error persists, please contact your local branch.",
          status: error.status,
          url: urlPath(error.url ?? ''),
          operation
        };
      }
    };
  }

  /**
   * Options for a page's own load of the booking named in its URL (40973). The support modal stays out: the page
   * handles the failure, through handleBookingLoadError.
   */
  public prepareBookingLoadOptions(payload: any): any {
    return { ...this.prepareOptions(payload), context: quietErrorsContext() };
  }

  /**
   * handleError for a page's own load of the booking named in its URL (40973). Not found (404) and an id that is not
   * a booking id (400) read as one plain message, which the API also gives for a booking that is not the caller's;
   * the modal sends the user home when it is dismissed. Anything else is handled as handleError does.
   */
  public handleBookingLoadError(operation = 'operation') {
    const handle = this.handleError(operation);
    return (error: HttpErrorResponse): Observable<any> => {
      if (error.status === 404 || error.status === 400) {
        throw {
          message: BOOKING_NOT_FOUND_MESSAGE,
          status: error.status,
          isValidationError: true,
          bookingNotFound: true
        };
      }
      return handle(error);
    };
  }

  public simulateError(): Observable<Response> {
    const error = new HttpErrorResponse({ status: 422 });
    return of(error) as any;
  }

}
